import React, { useMemo } from 'react'
import { Command } from 'cmdk'
import CmdKGroup from '../CmdKGroup'
import CommandItem from '../CmdKItem'
import { useQuery, gql } from '@apollo/client'
import { useOrgName } from 'src/hooks/useOrgName'
import { navigate, routes } from '@cedarjs/router'
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded'
import { filter } from '../filter'

const WORK_ORDERS_QUERY = gql`
  query WorkOrders($orgSlug: String!) {
    internalWorkOrders(
      orgSlug: $orgSlug
      statuses: [DRAFT, IN_PROGRESS, IN_REVIEW]
    ) {
      id
      name
      customName
      workOrderNumber
      status
      aircraft {
        id
        tailNumber
      }
      stats {
        totalMaintenanceWorkItems
        maintenanceWorkItemsInProgress
        maintenanceWorkItemsInReview
        totalCustomWorkItems
        customWorkItemsInProgress
        customWorkItemsInReview
      }
    }
  }
`

interface CommandMenuGroup {
  heading: string
  items: {
    label: string
    icon?: React.ReactNode
    subtext?: string
    disabled?: boolean
    onClick?: () => void
    children?: {
      label: string
      icon?: React.ReactNode
      subtext?: string
      onClick?: () => void
    }[]
  }[]
}

interface WorkOrderModuleProps {
  search: string
  onNavigate: (groups: CommandMenuGroup[]) => void
  onBack: () => void
  pages: CommandMenuGroup[][]
  onClose: () => void
}

const WorkOrderModule: React.FC<WorkOrderModuleProps> = ({
  onNavigate,
  pages,
  search,
  onClose,
}) => {
  const orgName = useOrgName()
  const { data, loading } = useQuery(WORK_ORDERS_QUERY, {
    variables: { orgSlug: orgName },
  })

  const handleWorkOrderClick = () => {
    onNavigate([
      {
        heading: 'Work Orders',
        items: workOrderItems,
      },
    ])
  }

  const workOrderItems = useMemo(() => {
    const items = []

    if (data?.internalWorkOrders) {
      items.push(
        ...data.internalWorkOrders.map((workOrder) => {
          return {
            label: `${workOrder.aircraft.tailNumber} - ${
              workOrder.customName ||
              workOrder.name ||
              `WO-${workOrder.workOrderNumber}`
            }`,
            subtext: 'Work Order',
            keywords: [
              workOrder.aircraft.tailNumber,
              workOrder.customName ||
                workOrder.name ||
                `wo-${workOrder.workOrderNumber}`,
              'work order',
            ],
            startIcon: <WorkOutlineRoundedIcon />,
            onClick: () => {
              onClose()
              navigate(routes.workOrder({ orgName, id: workOrder.id }))
            },
          }
        })
      )
    }

    return items
  }, [data?.internalWorkOrders, orgName, onClose])

  // Only show the top-level item when we're at root level
  if (pages.length > 0) {
    return null
  }

  const workOrderCount = data?.internalWorkOrders?.length || 0

  const filteredWorkOrderItems = workOrderItems.filter((item) =>
    filter(item.label, search, item.keywords)
  )

  if (filteredWorkOrderItems.length === 0) {
    return null
  }

  // If there's a search term, show items (or loading state)
  if (search) {
    if (data?.internalWorkOrders) {
      return (
        <CmdKGroup heading="Work Orders">
          {filteredWorkOrderItems.map((item) => (
            <CommandItem
              key={item.id}
              onSelect={item.onClick}
              startIcon={item.startIcon}
              subtext={item.subtext}
              keywords={item.keywords}
            >
              {item.label}
            </CommandItem>
          ))}
        </CmdKGroup>
      )
    }
  }

  // Show parent item if no search term
  return (
    <CmdKGroup heading="Work Orders">
      <CommandItem
        onSelect={handleWorkOrderClick}
        startIcon={<WorkOutlineRoundedIcon />}
        subtext={`${workOrderCount} active work orders`}
      >
        Work Orders
      </CommandItem>
    </CmdKGroup>
  )
}

export default WorkOrderModule
